<template>
  <section class="access-page" :dir="direction">
    <header class="section-head">
      <div>
        <h1>{{ t.title }}</h1>
        <p class="desc">{{ t.subtitle }}</p>
      </div>

      <button
        v-if="tab === 'users' && canUsers"
        type="button"
        class="header-button"
        @click="openUser()"
      >
        <v-icon size="19">mdi-account-plus-outline</v-icon>
        {{ t.addUser }}
      </button>
      <button
        v-else-if="tab === 'roles' && canRoles"
        type="button"
        class="header-button"
        @click="openRole()"
      >
        <v-icon size="19">mdi-shield-plus-outline</v-icon>
        {{ t.addRole }}
      </button>
    </header>

    <div v-if="notice" class="notice" :class="noticeKind" role="status">
      <v-icon size="20">{{
        noticeKind === "error"
          ? "mdi-alert-circle-outline"
          : "mdi-check-circle-outline"
      }}</v-icon>
      <span>{{ notice }}</span>
      <button
        type="button"
        class="notice-close"
        :aria-label="t.close"
        @click="notice = ''"
      >
        <v-icon size="18">mdi-close</v-icon>
      </button>
    </div>

    <div v-if="loading" class="loading-state" role="status">
      <span class="loading-spinner" aria-hidden="true"></span>
      {{ t.loading }}
    </div>

    <template v-else>
      <div class="stats-grid">
        <article v-if="canUsers" class="stat-card">
          <span class="stat-icon blue"
            ><v-icon size="23">mdi-account-group-outline</v-icon></span
          >
          <div>
            <span class="stat-label">{{ t.users }}</span
            ><strong>{{ users.length }}</strong>
          </div>
        </article>
        <article v-if="canUsers" class="stat-card">
          <span class="stat-icon green"
            ><v-icon size="23">mdi-account-check-outline</v-icon></span
          >
          <div>
            <span class="stat-label">{{ t.activeUsers }}</span
            ><strong>{{ activeUsers }}</strong>
          </div>
        </article>
        <article v-if="canRoles" class="stat-card">
          <span class="stat-icon violet"
            ><v-icon size="23">mdi-shield-account-outline</v-icon></span
          >
          <div>
            <span class="stat-label">{{ t.roles }}</span
            ><strong>{{ roles.length }}</strong>
          </div>
        </article>
      </div>

      <nav class="tabs" role="tablist" :aria-label="t.title">
        <button
          v-if="canUsers"
          id="access-users-tab"
          type="button"
          role="tab"
          :aria-selected="tab === 'users'"
          aria-controls="access-users-panel"
          :class="{ selected: tab === 'users' }"
          @click="tab = 'users'"
        >
          <v-icon size="19">mdi-account-group-outline</v-icon>
          {{ t.users }} <span class="tab-count">{{ users.length }}</span>
        </button>
        <button
          v-if="canRoles"
          id="access-roles-tab"
          type="button"
          role="tab"
          :aria-selected="tab === 'roles'"
          aria-controls="access-roles-panel"
          :class="{ selected: tab === 'roles' }"
          @click="tab = 'roles'"
        >
          <v-icon size="19">mdi-shield-account-outline</v-icon>
          {{ t.roles }} <span class="tab-count">{{ roles.length }}</span>
        </button>
      </nav>

      <section
        v-if="tab === 'users' && canUsers"
        id="access-users-panel"
        role="tabpanel"
        aria-labelledby="access-users-tab"
        class="panel"
      >
        <div class="panel-heading">
          <div>
            <h2>
              <v-icon size="18">mdi-account-group-outline</v-icon>{{ t.users }}
            </h2>
            <p>{{ t.usersDescription }}</p>
          </div>
          <label class="search-field">
            <v-icon size="20">mdi-magnify</v-icon>
            <input
              v-model.trim="search"
              type="search"
              :placeholder="t.searchUsers"
              :aria-label="t.searchUsers"
            />
          </label>
        </div>
        <div class="table-scroll">
          <table>
            <thead>
              <tr>
                <th scope="col">{{ t.username }}</th>
                <th scope="col">{{ t.nameAr }}</th>
                <th scope="col">{{ t.email }}</th>
                <th scope="col">{{ t.role }}</th>
                <th scope="col">{{ t.status }}</th>
                <th scope="col">{{ t.actions }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in filteredUsers" :key="user.id">
                <td :data-label="t.username">
                  <div class="user-cell">
                    <span class="avatar" aria-hidden="true">{{
                      userInitials(user)
                    }}</span>
                    <span class="username">{{ user.username }}</span>
                  </div>
                </td>
                <td :data-label="t.nameAr" class="name-cell">
                  {{ user.name_ar || user.name || "—" }}
                </td>
                <td :data-label="t.email" class="email-cell">
                  <span dir="ltr">{{ user.email || "—" }}</span>
                </td>
                <td :data-label="t.role">
                  <span class="role-chip">{{ localized(user.role) }}</span>
                </td>
                <td :data-label="t.status">
                  <span
                    class="status"
                    :class="user.is_active ? 'active' : 'inactive'"
                  >
                    <span class="status-dot"></span
                    >{{ user.is_active ? t.active : t.inactive }}
                  </span>
                </td>
                <td :data-label="t.actions">
                  <div class="actions">
                    <button
                      v-if="user.can_manage"
                      type="button"
                      class="action-button"
                      @click="openUser(user)"
                    >
                      <v-icon size="17">mdi-pencil-outline</v-icon>{{ t.edit }}
                    </button>
                    <button
                      v-if="user.can_manage"
                      type="button"
                      class="action-button"
                      @click="openPassword(user)"
                    >
                      <v-icon size="17">mdi-lock-reset</v-icon>{{ t.password }}
                    </button>
                    <button
                      v-if="
                        user.can_manage &&
                        user.is_active &&
                        user.id !== viewerId
                      "
                      type="button"
                      class="action-button danger"
                      :disabled="busy"
                      @click="askDisable(user)"
                    >
                      <v-icon size="17">mdi-account-off-outline</v-icon
                      >{{ t.disable }}
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="!filteredUsers.length" class="empty-state">
            <v-icon size="38">mdi-account-search-outline</v-icon>
            <span>{{ t.noData }}</span>
          </div>
        </div>
      </section>

      <section
        v-if="tab === 'roles' && canRoles"
        id="access-roles-panel"
        role="tabpanel"
        aria-labelledby="access-roles-tab"
        class="panel"
      >
        <div class="panel-heading">
          <div>
            <h2>
              <v-icon size="18">mdi-shield-account-outline</v-icon>{{ t.roles }}
            </h2>
            <p>{{ t.rolesDescription }}</p>
          </div>
        </div>
        <div v-if="roles.length" class="role-grid">
          <article v-for="role in roles" :key="role.id" class="role-card">
            <div class="role-card-top">
              <span class="role-icon"
                ><v-icon size="23">mdi-shield-account-outline</v-icon></span
              >
              <span
                class="status"
                :class="role.is_active ? 'active' : 'inactive'"
              >
                <span class="status-dot"></span
                >{{ role.is_active ? t.active : t.inactive }}
              </span>
            </div>
            <h3>{{ localized(role) }}</h3>
            <code>{{ role.code }}</code>
            <div class="role-meta">
              <span
                ><v-icon size="17">mdi-account-multiple-outline</v-icon
                >{{ role.users_count || 0 }} {{ t.users }}</span
              >
              <span
                ><v-icon size="17">mdi-key-outline</v-icon
                >{{ role.permission_ids?.length || 0 }}
                {{ t.permissions }}</span
              >
            </div>
            <button type="button" class="role-edit" @click="openRole(role)">
              <v-icon size="17">mdi-pencil-outline</v-icon
              >{{ t.editPermissions }}
            </button>
          </article>
        </div>
        <div v-else class="empty-state">
          <v-icon size="38">mdi-shield-off-outline</v-icon
          ><span>{{ t.noData }}</span>
        </div>
      </section>
    </template>

    <div v-if="userDialog" class="backdrop" @click.self="userDialog = false">
      <div
        class="modal"
        role="dialog"
        aria-modal="true"
        :aria-label="editUserId ? t.edit : t.addUser"
      >
        <div class="modal-heading">
          <span class="modal-icon"
            ><v-icon size="23">mdi-account-cog-outline</v-icon></span
          >
          <div>
            <h2>{{ editUserId ? t.edit : t.addUser }}</h2>
            <p>{{ t.usersDescription }}</p>
          </div>
          <button
            type="button"
            class="icon-close"
            :aria-label="t.close"
            @click="userDialog = false"
          >
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </div>
        <form @submit.prevent="saveUser">
          <div class="fields">
            <label class="form-field"
              >{{ t.username
              }}<input
                v-model.trim="userForm.username"
                required
                maxlength="100"
                autocomplete="off"
            /></label>
            <label class="form-field"
              >{{ t.nameAr
              }}<input v-model.trim="userForm.name_ar" required maxlength="200"
            /></label>
            <label class="form-field"
              >{{ t.nameEn
              }}<input v-model.trim="userForm.name_en" maxlength="200"
            /></label>
            <label class="form-field"
              >{{ t.email
              }}<input
                v-model.trim="userForm.email"
                required
                type="email"
                maxlength="200"
            /></label>
            <label class="form-field"
              >{{ t.role }}
              <select v-model.number="userForm.role_id" required>
                <option :value="null" disabled>{{ t.choose }}</option>
                <option
                  v-for="role in assignableRoles"
                  :key="role.id"
                  :value="role.id"
                >
                  {{ localized(role) }}
                </option>
              </select>
            </label>
            <template v-if="!editUserId">
              <label class="form-field"
                >{{ t.password
                }}<input
                  v-model="userForm.password"
                  required
                  type="password"
                  minlength="8"
                  autocomplete="new-password"
              /></label>
              <label class="form-field"
                >{{ t.confirm
                }}<input
                  v-model="userForm.password_confirmation"
                  required
                  type="password"
                  minlength="8"
                  autocomplete="new-password"
              /></label>
            </template>
            <label class="check status-check"
              ><input v-model="userForm.is_active" type="checkbox" /><span>{{
                t.active
              }}</span></label
            >
          </div>
          <p
            v-if="notice && noticeKind === 'error'"
            class="form-error"
            role="alert"
          >
            {{ notice }}
          </p>
          <div class="modal-actions">
            <button
              type="button"
              class="secondary-button"
              :disabled="busy"
              @click="userDialog = false"
            >
              {{ t.cancel }}
            </button>
            <button type="submit" class="primary-button" :disabled="busy">
              {{ busy ? t.saving : t.save }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <div
      v-if="passwordDialog"
      class="backdrop"
      @click.self="passwordDialog = false"
    >
      <div
        class="modal small"
        role="dialog"
        aria-modal="true"
        :aria-label="t.password"
      >
        <div class="modal-heading">
          <span class="modal-icon"
            ><v-icon size="23">mdi-lock-reset</v-icon></span
          >
          <div>
            <h2>{{ t.password }}</h2>
            <p>{{ t.passwordDescription }}</p>
          </div>
          <button
            type="button"
            class="icon-close"
            :aria-label="t.close"
            @click="passwordDialog = false"
          >
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </div>
        <form @submit.prevent="savePassword">
          <label class="form-field"
            >{{ t.password
            }}<input
              v-model="passwordForm.password"
              required
              type="password"
              minlength="8"
              autocomplete="new-password"
          /></label>
          <label class="form-field"
            >{{ t.confirm
            }}<input
              v-model="passwordForm.password_confirmation"
              required
              type="password"
              minlength="8"
              autocomplete="new-password"
          /></label>
          <p
            v-if="notice && noticeKind === 'error'"
            class="form-error"
            role="alert"
          >
            {{ notice }}
          </p>
          <div class="modal-actions">
            <button
              type="button"
              class="secondary-button"
              :disabled="busy"
              @click="passwordDialog = false"
            >
              {{ t.cancel }}
            </button>
            <button type="submit" class="primary-button" :disabled="busy">
              {{ busy ? t.saving : t.save }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="roleDialog" class="backdrop" @click.self="roleDialog = false">
      <div
        class="modal wide"
        role="dialog"
        aria-modal="true"
        :aria-label="editRoleId ? t.editPermissions : t.addRole"
      >
        <div class="modal-heading">
          <span class="modal-icon"
            ><v-icon size="23">mdi-shield-edit-outline</v-icon></span
          >
          <div>
            <h2>{{ editRoleId ? t.editPermissions : t.addRole }}</h2>
            <p>{{ t.rolesDescription }}</p>
          </div>
          <button
            type="button"
            class="icon-close"
            :aria-label="t.close"
            @click="roleDialog = false"
          >
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </div>
        <form @submit.prevent="saveRole">
          <div class="fields">
            <label class="form-field"
              >{{ t.code
              }}<input
                v-model.trim="roleForm.code"
                :disabled="!!editRoleId"
                required
                maxlength="50"
                pattern="[a-z][a-z0-9_]*"
            /></label>
            <label class="form-field"
              >{{ t.nameAr
              }}<input v-model.trim="roleForm.name_ar" required maxlength="100"
            /></label>
            <label class="form-field"
              >{{ t.nameEn
              }}<input v-model.trim="roleForm.name_en" required maxlength="100"
            /></label>
            <label class="form-field"
              >{{ t.description }}<input v-model.trim="roleForm.description"
            /></label>
            <label class="check status-check"
              ><input
                v-model="roleForm.is_active"
                type="checkbox"
                :disabled="roleForm.code === 'admin'"
              /><span>{{ t.active }}</span></label
            >
          </div>
          <div class="permissions-heading">
            <h3>{{ t.permissions }}</h3>
            <span
              >{{ roleForm.permission_ids.length }} /
              {{ permissions.length }}</span
            >
          </div>
          <div class="permission-grid">
            <fieldset v-for="group in permissionGroups" :key="group.name">
              <legend>{{ group.name }}</legend>
              <label
                v-for="permission in group.items"
                :key="permission.id"
                class="check permission-option"
              >
                <input
                  v-model="roleForm.permission_ids"
                  type="checkbox"
                  :value="permission.id"
                  :disabled="
                    roleForm.code === 'admin' &&
                    protectedCodes.includes(permission.code)
                  "
                />
                <span
                  >{{ localized(permission)
                  }}<small>{{ permission.code }}</small></span
                >
              </label>
            </fieldset>
          </div>
          <p
            v-if="notice && noticeKind === 'error'"
            class="form-error"
            role="alert"
          >
            {{ notice }}
          </p>
          <div class="modal-actions">
            <button
              type="button"
              class="secondary-button"
              :disabled="busy"
              @click="roleDialog = false"
            >
              {{ t.cancel }}
            </button>
            <button type="submit" class="primary-button" :disabled="busy">
              {{ busy ? t.saving : t.save }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <div
      v-if="disableTarget"
      class="backdrop"
      @click.self="disableTarget = null"
    >
      <div
        class="modal small"
        role="dialog"
        aria-modal="true"
        :aria-label="t.disable"
      >
        <div class="modal-heading">
          <span class="modal-icon danger-icon"
            ><v-icon size="23">mdi-account-off-outline</v-icon></span
          >
          <div>
            <h2>{{ t.disable }}</h2>
            <p>{{ disableTarget.username }}</p>
          </div>
          <button
            type="button"
            class="icon-close"
            :aria-label="t.close"
            @click="disableTarget = null"
          >
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </div>
        <div class="confirm-body">
          <p>{{ t.disableAsk }}</p>
          <p
            v-if="notice && noticeKind === 'error'"
            class="form-error"
            role="alert"
          >
            {{ notice }}
          </p>
          <div class="modal-actions">
            <button
              type="button"
              class="secondary-button"
              :disabled="busy"
              @click="disableTarget = null"
            >
              {{ t.cancel }}
            </button>
            <button
              type="button"
              class="danger-button"
              :disabled="busy"
              @click="disableUser"
            >
              {{ busy ? t.saving : t.disable }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import api from "@/plugins/api";
const { locale } = useI18n();
const direction = computed(() =>
  locale.value.startsWith("ar") ? "rtl" : "ltr"
);
const labels = {
  ar: {
    title: "إدارة الوصول",
    subtitle: "إدارة المستخدمين والأدوار",
    users: "المستخدمون",
    roles: "الأدوار",
    permissions: "الصلاحيات",
    activeUsers: "المستخدمون النشطون",
    usersDescription: "إدارة الحسابات وأدوار الوصول إلى النظام",
    rolesDescription: "تحديد الأدوار والصلاحيات الممنوحة لكل دور",
    passwordDescription: "تعيين كلمة مرور جديدة للحساب",
    searchUsers: "ابحث بالاسم أو البريد أو الدور...",
    addUser: "إضافة مستخدم",
    addRole: "إضافة دور",
    editPermissions: "تعديل الدور وصلاحياته",
    username: "اسم الدخول",
    nameAr: "الاسم بالعربية",
    nameEn: "الاسم بالإنجليزية",
    email: "البريد الإلكتروني",
    role: "الدور",
    status: "الحالة",
    actions: "الإجراءات",
    active: "نشط",
    inactive: "معطل",
    edit: "تعديل",
    password: "تغيير كلمة المرور",
    disable: "تعطيل",
    confirm: "تأكيد كلمة المرور",
    choose: "اختر الدور",
    code: "الرمز",
    description: "الوصف",
    save: "حفظ",
    saving: "جارٍ الحفظ...",
    cancel: "إلغاء",
    close: "إغلاق",
    search: "بحث",
    loading: "جاري التحميل...",
    noData: "لا توجد بيانات",
    saved: "تم الحفظ بنجاح",
    disabled: "تم تعطيل الحساب",
    disableAsk: "تعطيل هذا الحساب؟",
    mismatch: "تأكيد كلمة المرور غير مطابق",
    noAccess: "لا تملك صلاحية إدارة المستخدمين أو الأدوار",
  },
  en: {
    title: "Access management",
    subtitle: "Manage users and roles",
    users: "Users",
    roles: "Roles",
    permissions: "Permissions",
    activeUsers: "Active users",
    usersDescription: "Manage accounts and their system access roles",
    rolesDescription: "Set the permissions granted to each role",
    passwordDescription: "Set a new password for this account",
    searchUsers: "Search name, email or role...",
    addUser: "Add user",
    addRole: "Add role",
    editPermissions: "Edit role and permissions",
    username: "Username",
    nameAr: "Arabic name",
    nameEn: "English name",
    email: "Email",
    role: "Role",
    status: "Status",
    actions: "Actions",
    active: "Active",
    inactive: "Disabled",
    edit: "Edit",
    password: "Change password",
    disable: "Disable",
    confirm: "Confirm password",
    choose: "Choose role",
    code: "Code",
    description: "Description",
    save: "Save",
    saving: "Saving...",
    cancel: "Cancel",
    close: "Close",
    search: "Search",
    loading: "Loading...",
    noData: "No data",
    saved: "Saved",
    disabled: "Account disabled",
    disableAsk: "Disable this account?",
    mismatch: "Passwords do not match",
    noAccess: "You cannot manage users or roles",
  },
};
const t = computed(() => labels[direction.value === "rtl" ? "ar" : "en"]);
const protectedCodes = ["users.manage", "roles.manage"];
const granted = ref([]);
const viewerId = ref(null);
const canUsers = computed(() => granted.value.includes("users.manage"));
const canRoles = computed(() => granted.value.includes("roles.manage"));
const tab = ref("users");
const users = ref([]);
const assignableRoles = ref([]);
const roles = ref([]);
const permissions = ref([]);
const activeUsers = computed(
  () => users.value.filter((user) => Boolean(user.is_active)).length
);
const search = ref("");
const loading = ref(true);
const busy = ref(false);
const notice = ref("");
const noticeKind = ref("success");
const userDialog = ref(false);
const passwordDialog = ref(false);
const roleDialog = ref(false);
const disableTarget = ref(null);
const editUserId = ref(null);
const editRoleId = ref(null);
const passwordUserId = ref(null);
const emptyUser = () => ({
  username: "",
  name_ar: "",
  name_en: "",
  email: "",
  role_id: null,
  is_active: true,
  password: "",
  password_confirmation: "",
});
const emptyRole = () => ({
  code: "",
  name_ar: "",
  name_en: "",
  description: "",
  is_active: true,
  permission_ids: [],
});
const userForm = ref(emptyUser());
const roleForm = ref(emptyRole());
const passwordForm = ref({ password: "", password_confirmation: "" });
const filteredUsers = computed(() => {
  const term = search.value.toLocaleLowerCase();
  return users.value.filter(
    (user) =>
      !term ||
      [
        user.username,
        user.name_ar,
        user.name_en,
        user.email,
        user.role?.name_ar,
        user.role?.name_en,
      ].some((value) =>
        String(value || "")
          .toLocaleLowerCase()
          .includes(term)
      )
  );
});
const permissionGroups = computed(() => {
  const groups = new Map();
  permissions.value
    .filter((item) => item.is_active)
    .forEach((item) => {
      const name = item.code.split(".")[0];
      if (!groups.has(name)) groups.set(name, []);
      groups.get(name).push(item);
    });
  return Array.from(groups, ([name, items]) => ({ name, items }));
});
function localized(item) {
  return (
    item?.[direction.value === "rtl" ? "name_ar" : "name_en"] ||
    item?.code ||
    "—"
  );
}
function userInitials(user) {
  const name = (
    user.name_ar ||
    user.name_en ||
    user.name ||
    user.username ||
    ""
  ).trim();
  return (
    name
      .split(/\s+/u)
      .slice(0, 2)
      .map((part) => Array.from(part)[0] || "")
      .join("")
      .toUpperCase() || "؟"
  );
}
function data(response) {
  return response?.data ?? response;
}
function rows(response) {
  const result = data(response);
  return Array.isArray(result)
    ? result
    : Array.isArray(result?.data)
    ? result.data
    : [];
}
function errorMessage(error) {
  const body = error?.response?.data;
  return (
    Object.values(body?.errors || {})[0]?.[0] ||
    body?.message ||
    error?.message ||
    "Error"
  );
}
async function loadUsers() {
  users.value = rows(await api.get("/users"));
}
async function loadAssignableRoles() {
  assignableRoles.value = rows(await api.get("/users/roles"));
}
async function loadRoles() {
  roles.value = rows(await api.get("/roles"));
}
async function loadPermissions() {
  permissions.value = rows(await api.get("/permissions"));
}
async function load() {
  loading.value = true;
  try {
    const me = data(await api.get("/me/permissions"));
    granted.value = me.permissions || [];
    viewerId.value = me.id;
    tab.value = canUsers.value ? "users" : "roles";
    if (!canUsers.value && !canRoles.value) throw new Error(t.value.noAccess);
    const results = await Promise.allSettled([
      ...(canUsers.value ? [loadUsers(), loadAssignableRoles()] : []),
      ...(canRoles.value ? [loadRoles(), loadPermissions()] : []),
    ]);
    const failed = results.find((result) => result.status === "rejected");
    if (failed) throw failed.reason;
  } catch (error) {
    notice.value = errorMessage(error);
    noticeKind.value = "error";
  } finally {
    loading.value = false;
  }
}
async function commit(action, after) {
  busy.value = true;
  notice.value = "";
  try {
    await action();
    if (after) await after();
    notice.value = t.value.saved;
    noticeKind.value = "success";
    return true;
  } catch (error) {
    notice.value = errorMessage(error);
    noticeKind.value = "error";
    return false;
  } finally {
    busy.value = false;
  }
}
function openUser(user = null) {
  notice.value = "";
  editUserId.value = user?.id ?? null;
  userForm.value = user
    ? {
        ...emptyUser(),
        username: user.username,
        name_ar: user.name_ar || user.name,
        name_en: user.name_en || "",
        email: user.email,
        role_id: user.role_id,
        is_active: Boolean(user.is_active),
      }
    : emptyUser();
  userDialog.value = true;
}
async function saveUser() {
  if (
    !editUserId.value &&
    userForm.value.password !== userForm.value.password_confirmation
  ) {
    notice.value = t.value.mismatch;
    noticeKind.value = "error";
    return;
  }
  const payload = { ...userForm.value };
  if (editUserId.value) {
    delete payload.password;
    delete payload.password_confirmation;
  }
  await commit(
    () =>
      editUserId.value
        ? api.put("/users/" + editUserId.value, payload)
        : api.post("/users", payload),
    async () => {
      userDialog.value = false;
      await loadUsers();
    }
  );
}
function openPassword(user) {
  notice.value = "";
  passwordUserId.value = user.id;
  passwordForm.value = { password: "", password_confirmation: "" };
  passwordDialog.value = true;
}
async function savePassword() {
  if (
    passwordForm.value.password !== passwordForm.value.password_confirmation
  ) {
    notice.value = t.value.mismatch;
    noticeKind.value = "error";
    return;
  }
  await commit(
    () =>
      api.put(
        "/users/" + passwordUserId.value + "/password",
        passwordForm.value
      ),
    () => {
      passwordDialog.value = false;
    }
  );
}
function askDisable(user) {
  notice.value = "";
  disableTarget.value = user;
}
async function disableUser() {
  const user = disableTarget.value;
  if (!user) return;
  const success = await commit(
    () => api.delete("/users/" + user.id),
    loadUsers
  );
  if (success) {
    notice.value = t.value.disabled;
    disableTarget.value = null;
  }
}
function openRole(role = null) {
  notice.value = "";
  editRoleId.value = role?.id ?? null;
  roleForm.value = role
    ? {
        code: role.code,
        name_ar: role.name_ar,
        name_en: role.name_en,
        description: role.description || "",
        is_active: Boolean(role.is_active),
        permission_ids: (role.permission_ids || []).filter((id) =>
          permissions.value.some((p) => p.id === id && p.is_active)
        ),
      }
    : emptyRole();
  roleDialog.value = true;
}
async function saveRole() {
  const payload = { ...roleForm.value };
  if (editRoleId.value) delete payload.code;
  await commit(
    () =>
      editRoleId.value
        ? api.put("/roles/" + editRoleId.value, payload)
        : api.post("/roles", payload),
    async () => {
      roleDialog.value = false;
      await Promise.all([
        loadRoles(),
        ...(canUsers.value ? [loadAssignableRoles()] : []),
      ]);
    }
  );
}
onMounted(load);
</script>

<style scoped>
.access-page {
  --navy: #15355c;
  --bg: #f4f6f9;
  --surface: #ffffff;
  --surface-2: #f8fafc;
  --border: #dde4ec;
  --text: #1a2433;
  --text-2: #4c5b6e;
  --text-3: #7a8799;
  --warning: #b45309;
  --shadow-sm: 0 1px 2px rgba(13, 34, 64, 0.06);
  --radius: 12px;
  min-height: 100vh;
  padding: 22px 26px 60px;
  overflow-x: hidden;
  background: var(--bg);
  color: var(--text);
  font-family: "Inter", "Cairo", sans-serif;
}

.access-page[dir="rtl"] {
  font-family: "Cairo", "Inter", sans-serif;
}
.access-page * {
  box-sizing: border-box;
}
.access-page button,
.access-page input,
.access-page select {
  font: inherit;
}
.access-page button {
  cursor: pointer;
}
.access-page button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.access-page :is(button, input, select):focus-visible {
  outline: 2px solid #00a8e8;
  outline-offset: 2px;
}

.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;
}
.section-head h1 {
  margin: 0;
  color: var(--text);
  font-size: 23px;
  font-weight: 800;
  line-height: 1.5;
}
.section-head .desc {
  max-width: 850px;
  margin: 5px 0 0;
  color: var(--text-2);
  font-size: 13px;
  line-height: 1.8;
}
.header-button,
.primary-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  padding: 8px 15px;
  border: 1px solid var(--navy);
  border-radius: 9px;
  background: var(--navy);
  color: #fff;
  font-size: 13px;
  font-weight: 800;
  white-space: nowrap;
}
.header-button:hover,
.primary-button:hover:not(:disabled) {
  background: #214c7c;
}

.notice {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  padding: 11px 13px;
  border: 1px solid #b6dec9;
  border-radius: var(--radius);
  background: #edf8f1;
  color: #19603d;
  font-size: 13px;
  font-weight: 700;
  overflow-wrap: anywhere;
}
.notice.error {
  border-color: #efc4ca;
  background: #fff1f2;
  color: #a33743;
}
.notice span {
  flex: 1;
}
.notice-close,
.icon-close {
  display: grid;
  place-items: center;
  flex: none;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: inherit;
}
.notice-close:hover,
.icon-close:hover {
  background: var(--surface-2);
}
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 180px;
  color: var(--text-3);
  font-size: 13px;
  font-weight: 700;
}
.loading-spinner {
  width: 22px;
  height: 22px;
  border: 3px solid var(--border);
  border-top-color: var(--navy);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}
.stat-card,
.panel,
.role-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-inline-start: 4px solid var(--navy);
  border-radius: var(--radius);
  box-shadow: var(--shadow-sm);
}
.stat-card {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  min-height: 86px;
  padding: 14px 16px;
}
.stat-icon,
.role-icon,
.modal-icon {
  display: grid;
  place-items: center;
  flex: none;
  width: 42px;
  height: 42px;
  border-radius: 9px;
  background: #edf2f7;
  color: var(--navy);
}
.stat-card > div {
  min-width: 0;
}
.stat-label {
  display: block;
  color: var(--text-3);
  font-size: 12px;
  font-weight: 700;
}
.stat-card strong {
  display: block;
  color: var(--text);
  font-size: 21px;
  font-weight: 800;
  line-height: 1.35;
  font-variant-numeric: tabular-nums;
}

.tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  width: fit-content;
  max-width: 100%;
  margin-bottom: 16px;
  padding: 4px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-sm);
}
.tabs button {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 36px;
  padding: 7px 13px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--text-2);
  font-size: 13px;
  font-weight: 700;
}
.tabs button:hover {
  background: var(--surface-2);
}
.tabs button.selected {
  background: var(--navy);
  color: #fff;
}
.tab-count {
  padding: 1px 6px;
  border-radius: 6px;
  background: rgba(122, 135, 153, 0.16);
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}
.tabs button.selected .tab-count {
  background: rgba(255, 255, 255, 0.18);
}

.panel {
  min-width: 0;
  margin-bottom: 16px;
  padding: 18px;
}
.panel-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}
.panel-heading h2 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: var(--text);
  font-size: 15.5px;
  font-weight: 800;
  line-height: 1.5;
}
.panel-heading h2 :deep(.v-icon) {
  flex-shrink: 0;
  color: var(--navy);
}
.panel-heading p {
  margin: 4px 0 0;
  color: var(--text-2);
  font-size: 13px;
  line-height: 1.8;
}
.search-field {
  display: flex;
  align-items: center;
  gap: 8px;
  width: min(310px, 100%);
  min-width: 220px;
  height: 40px;
  padding: 0 11px;
  border: 1px solid var(--border);
  border-radius: 9px;
  background: var(--surface-2);
  color: var(--text-3);
}
.search-field:focus-within {
  border-color: var(--navy);
}
.search-field input {
  width: 100%;
  min-width: 0;
  height: 100%;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--text);
  font-size: 13px;
}
.search-field input:focus-visible {
  outline: none;
}
.search-field input::placeholder {
  color: var(--text-3);
}

.table-scroll {
  overflow-x: auto;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  -webkit-overflow-scrolling: touch;
}
table {
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
  background: var(--surface);
  font-size: 13px;
  text-align: start;
}
thead th {
  padding: 11px 12px;
  color: #fff;
  font-weight: 700;
  text-align: start;
  white-space: nowrap;
  background: var(--navy);
}
tbody td {
  padding: 11px 12px;
  color: var(--text-2);
  vertical-align: middle;
  line-height: 1.7;
  border-top: 1px solid var(--border);
  text-align: start;
}
tbody tr:hover {
  background: var(--surface-2);
}
.user-cell {
  display: flex;
  align-items: center;
  gap: 9px;
}
.avatar {
  display: grid;
  place-items: center;
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #edf2f7;
  color: var(--navy);
  font-size: 12px;
  font-weight: 800;
}
.username,
.name-cell {
  color: var(--text);
  font-weight: 700;
}
.email-cell span {
  unicode-bidi: isolate;
  overflow-wrap: anywhere;
}
.role-chip,
.status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 7px;
  background: var(--surface-2);
  color: var(--navy);
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}
.status {
  border-radius: 999px;
}
.status.active {
  background: #eaf5ee;
  color: #1d6b45;
}
.status.inactive {
  background: #fff1f2;
  color: #a54550;
}
.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}
.actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}
.action-button {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 31px;
  padding: 4px 8px;
  border: 1px solid var(--border);
  border-radius: 7px;
  background: var(--surface);
  color: var(--navy);
  font-size: 11.5px;
  font-weight: 700;
  white-space: nowrap;
}
.action-button:hover {
  background: var(--surface-2);
}
.action-button.danger {
  color: #a54550;
}
code {
  display: inline-block;
  padding: 3px 6px;
  border-radius: 6px;
  background: var(--surface-2);
  color: var(--navy);
  direction: ltr;
  unicode-bidi: isolate;
  font: 600 11px ui-monospace, SFMono-Regular, Consolas, monospace;
  overflow-wrap: anywhere;
}
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 150px;
  padding: 20px;
  color: var(--text-3);
  font-size: 13px;
  font-weight: 700;
  text-align: center;
}

.role-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(245px, 1fr));
  gap: 12px;
}
.role-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 14px;
}
.role-card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}
.role-card h3 {
  margin: 12px 0 5px;
  color: var(--text);
  font-size: 15.5px;
  font-weight: 800;
}
.role-card code {
  align-self: flex-start;
}
.role-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 15px;
  margin: 13px 0;
  padding-top: 12px;
  border-top: 1px solid var(--border);
  color: var(--text-2);
  font-size: 12px;
}
.role-meta span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.role-edit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 35px;
  margin-top: auto;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface-2);
  color: var(--navy);
  font-size: 12px;
  font-weight: 800;
}
.role-edit:hover {
  background: #edf2f7;
}

.backdrop {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 22px;
  background: rgba(15, 30, 48, 0.62);
  backdrop-filter: blur(3px);
  overscroll-behavior: contain;
}
.modal {
  flex: none;
  width: min(720px, 100%);
  max-height: calc(100dvh - 44px);
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable both-edges;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: 0 20px 54px rgba(13, 34, 64, 0.22);
}
.modal::-webkit-scrollbar {
  width: 6px;
}
.modal::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 20px;
}
.modal.small {
  width: min(470px, 100%);
}
.modal.wide {
  width: min(840px, 100%);
}
.modal-heading {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px;
  border-bottom: 1px solid var(--border);
}
.modal-heading > div {
  flex: 1;
  min-width: 0;
}
.modal-heading h2 {
  margin: 0;
  color: var(--text);
  font-size: 15.5px;
  font-weight: 800;
}
.modal-heading p {
  margin: 4px 0 0;
  color: var(--text-2);
  font-size: 12px;
  line-height: 1.7;
}
.modal-icon.danger-icon {
  background: #fff1f2;
  color: #a54550;
}
.modal form,
.confirm-body {
  padding: 18px;
}
.confirm-body > p:first-child {
  margin: 0;
  color: var(--text-2);
  font-size: 13px;
  line-height: 1.9;
}
.fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 18px;
}
.form-field {
  display: block;
  min-width: 0;
  margin-bottom: 14px;
  color: var(--text);
  font-size: 13px;
  font-weight: 700;
}
.fields .form-field {
  margin-bottom: 0;
}
.form-field input,
.form-field select {
  display: block;
  width: 100%;
  min-width: 0;
  height: 42px;
  margin-top: 6px;
  padding: 8px 11px;
  border: 1px solid var(--border);
  border-radius: 9px;
  background: var(--surface-2);
  color: var(--text);
  font-size: 13px;
}
.form-field input:disabled,
.form-field select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.form-field input:focus,
.form-field select:focus {
  border-color: var(--navy);
}
.check {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
}
.check input {
  flex: none;
  width: 17px;
  height: 17px;
  margin: 2px 0 0;
  accent-color: #087f86;
  cursor: pointer;
}
.check input:disabled {
  cursor: not-allowed;
}
.status-check {
  align-items: center;
  align-self: end;
  min-height: 42px;
  padding: 8px 11px;
  border: 1px solid var(--border);
  border-radius: 9px;
  background: var(--surface-2);
  color: var(--text);
  font-size: 13px;
  font-weight: 700;
}
.status-check input {
  margin: 0;
}
.modal.small .status-check {
  margin: 4px 0 14px;
}
.permissions-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin: 22px 0 12px;
}
.permissions-heading h3 {
  margin: 0;
  color: var(--text);
  font-size: 15.5px;
  font-weight: 800;
}
.permissions-heading span {
  color: var(--text-3);
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}
.permission-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
  gap: 12px;
}
.permission-grid fieldset {
  min-width: 0;
  margin: 0;
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--surface-2);
}
.permission-grid legend {
  padding-inline: 5px;
  color: var(--navy);
  font-size: 12px;
  font-weight: 800;
}
.permission-option {
  align-items: flex-start;
  margin: 6px 0;
  padding: 7px 5px;
  border-radius: 7px;
  color: var(--text);
  font-size: 12px;
  font-weight: 700;
}
.permission-option:hover {
  background: var(--surface);
}
.permission-option span {
  min-width: 0;
  overflow-wrap: anywhere;
}
.permission-option small {
  display: block;
  margin-top: 2px;
  color: var(--text-3);
  direction: ltr;
  unicode-bidi: isolate;
  text-align: start;
  font: 11px ui-monospace, SFMono-Regular, Consolas, monospace;
}
.form-error {
  margin: 14px 0 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fff1f2;
  color: #a33743;
  font-size: 12px;
  font-weight: 700;
  overflow-wrap: anywhere;
}
.modal-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--border);
}
.secondary-button,
.danger-button {
  min-height: 40px;
  padding: 8px 15px;
  border: 1px solid var(--border);
  border-radius: 9px;
  background: var(--surface);
  color: var(--text);
  font-size: 13px;
  font-weight: 700;
}
.secondary-button:hover {
  background: var(--surface-2);
}
.danger-button {
  border-color: #a54550;
  background: #a54550;
  color: #fff;
}
.danger-button:hover:not(:disabled) {
  background: #8f3642;
}

:global(html[data-theme="dark"]) .access-page,
:global(.v-theme--dark) .access-page {
  --bg: #0f172a;
  --surface: #111827;
  --surface-2: #1e293b;
  --border: #334155;
  --text: #e2e8f0;
  --text-2: #cbd5e1;
  --text-3: #94a3b8;
  --navy: #24558b;
  --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.15);
}
:global(html[data-theme="dark"])
  .access-page
  :is(.stat-icon, .role-icon, .modal-icon, .avatar, .role-chip),
:global(.v-theme--dark)
  .access-page
  :is(.stat-icon, .role-icon, .modal-icon, .avatar, .role-chip) {
  background: #253b58;
  color: #dbeafe;
}
:global(html[data-theme="dark"]) .access-page .status.active,
:global(.v-theme--dark) .access-page .status.active {
  background: #1b473b;
  color: #a7ebc8;
}
:global(html[data-theme="dark"])
  .access-page
  :is(.status.inactive, .notice.error, .form-error),
:global(.v-theme--dark)
  .access-page
  :is(.status.inactive, .notice.error, .form-error) {
  background: #4b2933;
  color: #ffc0c6;
}

@media (max-width: 1100px) {
  .stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 768px) {
  .access-page {
    padding: 16px 10px 40px;
  }
  .section-head {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 15px;
  }
  .section-head h1 {
    font-size: 21px;
  }
  .section-head .desc {
    font-size: 12px;
    line-height: 1.75;
  }
  .header-button {
    width: 100%;
  }
  .stats-grid {
    gap: 10px;
    margin-bottom: 12px;
  }
  .stat-card {
    min-height: 76px;
    padding: 11px;
  }
  .tabs {
    width: 100%;
    margin-bottom: 12px;
  }
  .tabs button {
    flex: 1;
    justify-content: center;
    padding: 7px;
    font-size: 12px;
  }
  .panel {
    padding: 14px;
    margin-bottom: 12px;
    border-radius: 14px;
  }
  .panel-heading {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
  }
  .panel-heading h2 {
    font-size: 15px;
  }
  .panel-heading p {
    font-size: 12px;
  }
  .search-field {
    width: 100%;
    min-width: 0;
  }
  .role-grid {
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  }
  .table-scroll {
    overflow: visible;
    border: 0;
  }
  table,
  tbody,
  tbody tr,
  td {
    display: block;
    width: 100%;
    min-width: 0;
  }
  thead {
    display: none;
  }
  tbody tr {
    margin-bottom: 12px;
    overflow: hidden;
    border: 1px solid var(--border);
    border-inline-start: 4px solid var(--navy);
    border-radius: 12px;
    background: var(--surface);
  }
  tbody tr:last-child {
    margin-bottom: 0;
  }
  tbody td {
    display: grid;
    grid-template-columns: 105px minmax(0, 1fr);
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-top: 0;
    border-bottom: 1px dashed var(--border);
    font-size: 12px;
    overflow-wrap: anywhere;
  }
  tbody td:last-child {
    border-bottom: 0;
  }
  tbody td::before {
    content: attr(data-label);
    color: var(--text-3);
    font-size: 11px;
    font-weight: 700;
  }
  .actions {
    min-width: 0;
  }
  .fields,
  .permission-grid {
    grid-template-columns: 1fr;
  }
  .backdrop {
    padding: 14px;
  }
  .modal {
    max-height: calc(100dvh - 28px);
  }
  .modal-heading,
  .modal form,
  .confirm-body {
    padding: 14px;
  }
}
@media (max-width: 420px) {
  .access-page {
    padding-inline: 8px;
  }
  .tabs button {
    gap: 4px;
    padding-inline: 4px;
  }
  .tabs button .v-icon {
    display: none;
  }
  .stat-icon {
    width: 36px;
    height: 36px;
  }
  .stat-card strong {
    font-size: 18px;
  }
  tbody td {
    grid-template-columns: 1fr;
    gap: 4px;
  }
  .modal-actions > button {
    flex: 1;
  }
}
@media (prefers-reduced-motion: reduce) {
  .loading-spinner {
    animation-duration: 1.5s;
  }
}
</style>
